import Phaser from "phaser";
import type { Controls } from "./Controls";

/**
 * Контроллер для геймпада (Xbox-like layout).
 * Phaser использует Web Gamepad API.
 *
 * Стандартный mapping:
 *   - Левый стик / D-Pad → направления
 *   - A button (idx 0) → punch
 *   - B button (idx 1) → block
 *   - X button (idx 2) → kick
 *   - Y button (idx 3) → (зарезерв.)
 *   - Start (idx 9) → старт/выбор
 *
 * Названия "A/B/X/Y" путаются между Xbox и Sony (Cross/Circle/Square/Triangle).
 * Phaser Pad.A/B/X/Y соответствуют индексам 0/1/2/3, что = Xbox layout.
 *
 * Для китайских USB-геймпадов часто индексы те же, но иногда A↔B свопнуты.
 * Если потребуется — переопределить через --gamepad-mapping флаг или config.
 */
export class GamepadControls implements Controls {
  readonly source: string;
  private scene: Phaser.Scene;
  private padIndex: number;
  private pad: Phaser.Input.Gamepad.Gamepad | null = null;
  private prev: Record<string, boolean> = {};
  private cur:  Record<string, boolean> = {};
  private deadzone = 0.35;

  constructor(scene: Phaser.Scene, padIndex: number) {
    this.scene = scene;
    this.padIndex = padIndex;
    this.source = `GAMEPAD ${padIndex + 1}`;
  }

  /** Подключён ли реально геймпад с этим индексом. */
  isConnected(): boolean {
    const gp = this.scene.input.gamepad;
    if (!gp) return false;
    const list = gp.gamepads;
    return !!list[this.padIndex] && list[this.padIndex]!.connected;
  }

  update(_time: number): void {
    this.prev = { ...this.cur };

    const gp = this.scene.input.gamepad;
    if (!gp) {
      this.pad = null;
      this.cur = {};
      return;
    }
    const list = gp.gamepads;
    this.pad = list[this.padIndex] ?? null;
    if (!this.pad || !this.pad.connected) {
      this.cur = {};
      return;
    }

    const lx = this.pad.leftStick.x;
    const ly = this.pad.leftStick.y;

    this.cur = {
      left:  this.pad.left  || lx < -this.deadzone,
      right: this.pad.right || lx >  this.deadzone,
      up:    this.pad.up    || ly < -this.deadzone,
      down:  this.pad.down  || ly >  this.deadzone,
      punch: this.pad.A,            // нижняя кнопка
      kick:  this.pad.X,            // левая кнопка
      block: this.pad.B,            // правая кнопка
      start: !!this.pad.buttons[9]?.pressed, // start
    };
  }

  isLeft()  { return !!this.cur.left; }
  isRight() { return !!this.cur.right; }
  isUp()    { return !!this.cur.up; }
  isDown()  { return !!this.cur.down; }
  isPunch() { return !!this.cur.punch; }
  isKick()  { return !!this.cur.kick; }
  isBlock() { return !!this.cur.block; }

  justPressed(action: string): boolean {
    return !!this.cur[action] && !this.prev[action];
  }
}
